import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Keyboard,
  LifeBuoy,
  LogOut,
  Settings,
  SunMoon,
  User,
} from "lucide-react";
import useUsuario from "../../../core/hooks/UseUsuario";
import AuthorizationService from "../../auth/Authorization.service";
import "./ProfileMenu.css";

/**
 * Menu de perfil em vidro líquido, ancorado no canto inferior esquerdo.
 *
 * Substitui o `SidebarUserCard` que ficava no pé do rail: os dois ocupam
 * exatamente o mesmo canto, e dois avatares ali seriam um sobre o outro.
 *
 * Sem primitiva headless no projeto (não há Radix nem equivalente próprio, e o
 * Dropdown do antd não dá foco rotativo, typeahead nem trap), a navegação por
 * teclado é implementada aqui.
 *
 * A superfície é escura nos dois temas de propósito — ver o cabeçalho de
 * ProfileMenu.css para a medição de contraste que levou a isso.
 */

export type Presence = "online" | "away";

export interface ProfileMenuProps {
  /** Sobrescreve o usuário da sessão. Existe para a story e para os testes. */
  user?: { name: string; email?: string };
  /** Presença fixa. Omitido, o componente deriva de ociosidade/aba oculta. */
  presence?: Presence;
  /** Desliga o relógio de ociosidade (testes e story). */
  trackIdle?: boolean;
}

/** Minutos de silêncio até a presença virar "away". */
const IDLE_MS = 45_000;

function initialOf(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed[0].toUpperCase() : "?";
}

/** `⌘` no Mac, `Ctrl` no resto — decidido uma vez, no módulo. */
function isMacLike(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
}

/**
 * `true` quando o foco está num campo de texto.
 *
 * Os atalhos globais (`⌘,` e `?`) não podem disparar enquanto alguém digita —
 * `?` é um caractere comum, e o editor de markdown das questões é um textarea.
 */
function digitando(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    el.isContentEditable === true
  );
}

export default function ProfileMenu({
  user,
  presence: presenceProp,
  trackIdle = true,
}: ProfileMenuProps) {
  const navigate = useNavigate();
  const { usuario } = useUsuario();

  const [open, setOpen] = useState(false);
  /**
   * O conteúdo do painel só entra no DOM na primeira abertura, e fica.
   *
   * O painel precisa permanecer montado para a transição de fechamento ter de
   * onde sair — mas o texto dele não precisa existir antes de alguém abrir, e
   * existir cobra caro: este componente é global, e um `getByText` solto em
   * qualquer outra spec passa a casar "Atalhos do teclado" sem querer (foi o
   * que aconteceu com o "ATALHOS" da coluna da moderação).
   */
  const [montado, setMontado] = useState(false);
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );
  const [presenceAuto, setPresenceAuto] = useState<Presence>("online");
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const avatarRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  /** Buffer do typeahead e o timer que o esvazia. */
  const typeahead = useRef({ buffer: "", timer: 0 });

  const presence = presenceProp ?? presenceAuto;

  const name =
    user?.name || usuario?.userName || usuario?.fullName || "Usuário";
  const email = user?.email ?? usuario?.email ?? "";

  /* ─────────────────────────── tema ─────────────────────────── */

  // Acompanha a classe `dark` no <html>, que é o mecanismo de tema do produto
  // (Topbar/AppBar a alternam e persistem em localStorage). Observar em vez de
  // guardar só estado local mantém o switch certo quando o tema muda por outro
  // controle.
  useEffect(() => {
    const ler = () =>
      setIsDark(document.documentElement.classList.contains("dark"));
    const obs = new MutationObserver(ler);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    ler();
    return () => obs.disconnect();
  }, []);

  const toggleTheme = useCallback(() => {
    const next = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
    setIsDark(next);
  }, []);

  /* ─────────────────────────── presença ─────────────────────────── */

  useEffect(() => {
    if (!trackIdle || presenceProp) return;
    let timer = window.setTimeout(() => setPresenceAuto("away"), IDLE_MS);

    const acordar = () => {
      window.clearTimeout(timer);
      if (document.visibilityState === "hidden") {
        setPresenceAuto("away");
        return;
      }
      setPresenceAuto("online");
      timer = window.setTimeout(() => setPresenceAuto("away"), IDLE_MS);
    };

    const eventos = ["pointerdown", "keydown", "visibilitychange"] as const;
    eventos.forEach((e) =>
      (e === "visibilitychange" ? document : window).addEventListener(e, acordar),
    );
    return () => {
      window.clearTimeout(timer);
      eventos.forEach((e) =>
        (e === "visibilitychange" ? document : window).removeEventListener(
          e,
          acordar,
        ),
      );
    };
  }, [trackIdle, presenceProp]);

  /* ─────────────────────────── itens ─────────────────────────── */

  const itens = useMemo(() => {
    const mod = isMacLike() ? "⌘ ," : "Ctrl ,";
    return [
      {
        id: "perfil",
        label: "Perfil",
        icon: <User size={18} aria-hidden />,
        onSelect: () => navigate("/perfil"),
      },
      {
        id: "config",
        label: "Configurações",
        icon: <Settings size={18} aria-hidden />,
        kbd: mod,
        onSelect: () => navigate("/perfil"),
      },
      {
        id: "aparencia",
        label: "Aparência",
        icon: <SunMoon size={18} aria-hidden />,
        checkbox: true,
        onSelect: toggleTheme,
      },
      {
        id: "atalhos",
        label: "Atalhos do teclado",
        icon: <Keyboard size={18} aria-hidden />,
        kbd: "?",
        onSelect: () => setShortcutsOpen(true),
      },
      {
        id: "ajuda",
        label: "Ajuda",
        icon: <LifeBuoy size={18} aria-hidden />,
        trailingIcon: <ChevronRight size={16} aria-hidden />,
        onSelect: () => navigate("/contato"),
      },
      {
        id: "sair",
        label: "Sair",
        icon: <LogOut size={18} aria-hidden />,
        danger: true,
        onSelect: () => AuthorizationService.logoutAndRedirect(),
      },
    ];
  }, [navigate, toggleTheme]);

  /* ─────────────────────── abrir / fechar / foco ─────────────────────── */

  const focaveis = useCallback(
    () =>
      Array.from(
        itemsRef.current?.querySelectorAll<HTMLButtonElement>(
          '[role="menuitem"], [role="menuitemcheckbox"]',
        ) ?? [],
      ),
    [],
  );

  const focarIndice = useCallback(
    (i: number) => {
      const lista = focaveis();
      if (!lista.length) return;
      const alvo = (i + lista.length) % lista.length;
      lista[alvo]?.focus();
    },
    [focaveis],
  );

  const abrir = useCallback(
    (foco: "primeiro" | "ultimo" | "nenhum" = "primeiro") => {
      // Dois commits na primeira vez: o conteúdo nasce no estado fechado
      // (blur 8px) e só então `data-open` vira true, para a animação ter de
      // onde partir em vez de aparecer pronta.
      setMontado(true);
      setOpen(true);
      if (foco === "nenhum") return;
      // Depois da pintura: o painel sai de `visibility: hidden` e um elemento
      // invisível não aceita foco.
      requestAnimationFrame(() => {
        const lista = focaveis();
        (foco === "ultimo" ? lista[lista.length - 1] : lista[0])?.focus();
      });
    },
    [focaveis],
  );

  const fechar = useCallback((devolverFoco: boolean) => {
    setOpen(false);
    if (devolverFoco) avatarRef.current?.focus();
  }, []);

  // Clique fora fecha, mas NÃO rouba o foco: o usuário já está indo para onde
  // clicou.
  useEffect(() => {
    if (!open) return;
    const aoApontar = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) fechar(false);
    };
    document.addEventListener("pointerdown", aoApontar);
    return () => document.removeEventListener("pointerdown", aoApontar);
  }, [open, fechar]);

  /* ─────────────────────── atalhos globais ─────────────────────── */

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (digitando(e.target)) return;
      const mod = isMacLike() ? e.metaKey : e.ctrlKey;
      if (mod && e.key === ",") {
        e.preventDefault();
        navigate("/perfil");
        return;
      }
      if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [navigate]);

  /* ─────────────────────── teclado do menu ─────────────────────── */

  const aoTeclarNoMenu = (e: React.KeyboardEvent) => {
    const lista = focaveis();
    const atual = lista.indexOf(document.activeElement as HTMLButtonElement);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focarIndice(atual + 1);
        return;
      case "ArrowUp":
        e.preventDefault();
        focarIndice(atual - 1);
        return;
      case "Home":
        e.preventDefault();
        focarIndice(0);
        return;
      case "End":
        e.preventDefault();
        focarIndice(lista.length - 1);
        return;
      case "Escape":
        e.preventDefault();
        fechar(true);
        return;
      case "Tab":
        // Trap: Tab e Shift+Tab circulam dentro dos itens.
        e.preventDefault();
        focarIndice(atual + (e.shiftKey ? -1 : 1));
        return;
      default:
        break;
    }

    // Typeahead: um caractere imprimível pula para o próximo item cujo rótulo
    // comece por ele. O buffer acumula por 600ms para "co" achar
    // "Configurações" e não parar em "Conf…" a cada tecla.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      window.clearTimeout(typeahead.current.timer);
      typeahead.current.buffer += e.key.toLowerCase();
      typeahead.current.timer = window.setTimeout(() => {
        typeahead.current.buffer = "";
      }, 600);

      const prefixo = typeahead.current.buffer;
      const ordem = lista
        .map((_, i) => lista[(atual + 1 + i) % lista.length])
        .filter(Boolean);
      const achado = ordem.find((el) =>
        (el.dataset.label ?? "").toLowerCase().startsWith(prefixo),
      );
      if (achado) {
        e.preventDefault();
        achado.focus();
      }
    }
  };

  const aoTeclarNoAvatar = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      abrir("primeiro");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      abrir("ultimo");
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      fechar(true);
    }
  };

  const nomeId = "acertei-profile-menu-name";

  return (
    <div
      ref={rootRef}
      className="acertei-profile-menu"
      /* O vidro é translúcido de propósito: as varreduras de cor do e2e
         comparam cor computada com a paleta, e nenhuma cor de vidro é um token
         do produto. Este é o escape documentado da própria varredura. */
      data-token-exempt
      data-testid="profile-menu"
    >
      <div
        id="acertei-profile-menu-panel"
        className="acertei-profile-menu__panel"
        data-open={open ? "true" : "false"}
        role="dialog"
        aria-modal="false"
        aria-labelledby={nomeId}
        /* Fechado, sai da ordem de tabulação e das leituras de tela — o painel
           continua no DOM só para a transição acontecer. */
        {...(open ? {} : { inert: "", "aria-hidden": true })}
      >
        <div className="acertei-profile-menu__content">
          {montado && (
          <>
          <div className="acertei-profile-menu__header">
            <span
              className="acertei-profile-menu__circle acertei-profile-menu__circle--lg"
              aria-hidden
            >
              {initialOf(name)}
              <span
                className="acertei-profile-menu__status-dot"
                data-presence={presence}
                data-testid="profile-menu-status"
              />
            </span>
            <div className="acertei-profile-menu__identity">
              <p className="acertei-profile-menu__name" id={nomeId}>
                {name}
              </p>
              {email && <p className="acertei-profile-menu__email">{email}</p>}
              <span className="acertei-profile-menu__presence">
                <span
                  className="acertei-profile-menu__presence-dot"
                  style={{
                    background:
                      presence === "online"
                        ? "var(--glass-status-online)"
                        : "var(--glass-status-away)",
                  }}
                  aria-hidden
                />
                {presence === "online" ? "Online" : "Ausente"}
              </span>
            </div>
          </div>

          <div className="acertei-profile-menu__divider" />

          <div
            ref={itemsRef}
            role="menu"
            aria-labelledby={nomeId}
            onKeyDown={aoTeclarNoMenu}
          >
            {itens.map((item) => (
              <div key={item.id}>
                {/* separador antes do "Sair", dentro do menu */}
                {item.danger && (
                  <div
                    role="separator"
                    className="acertei-profile-menu__divider"
                  />
                )}
                <button
                  type="button"
                  role={item.checkbox ? "menuitemcheckbox" : "menuitem"}
                  {...(item.checkbox ? { "aria-checked": isDark } : {})}
                  tabIndex={-1}
                  data-label={item.label}
                  data-testid={`profile-menu-item-${item.id}`}
                  className={
                    "acertei-profile-menu__item" +
                    (item.danger ? " acertei-profile-menu__item--danger" : "")
                  }
                  // O ponteiro move o foco para o item sob ele: sem isto o
                  // hover pinta uma pílula e o foco do teclado pinta outra, e
                  // aparecem duas ao mesmo tempo.
                  onPointerMove={(e) => {
                    if (document.activeElement !== e.currentTarget)
                      e.currentTarget.focus();
                  }}
                  onClick={() => {
                    item.onSelect();
                    // A Aparência mantém o menu aberto; todo o resto fecha.
                    if (!item.checkbox) fechar(false);
                  }}
                >
                  <span className="acertei-profile-menu__item-icon">
                    {item.icon}
                  </span>
                  <span className="acertei-profile-menu__item-label">
                    {item.label}
                    {item.checkbox && (
                      <span className="acertei-profile-menu__item-sub">
                        {isDark ? "Escuro" : "Claro"}
                      </span>
                    )}
                  </span>
                  {item.kbd && (
                    <kbd className="acertei-profile-menu__kbd">{item.kbd}</kbd>
                  )}
                  {item.checkbox && (
                    <span className="acertei-profile-menu__switch" aria-hidden />
                  )}
                  {item.trailingIcon && (
                    <span className="acertei-profile-menu__item-icon">
                      {item.trailingIcon}
                    </span>
                  )}
                </button>
              </div>
            ))}
          </div>
          </>
          )}
        </div>
      </div>

      <button
        ref={avatarRef}
        type="button"
        className="acertei-profile-menu__avatar"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="acertei-profile-menu-panel"
        /* O nome já está aqui; por isso o trecho revelável é `aria-hidden` —
           senão o leitor de tela anuncia o nome duas vezes. */
        aria-label={`${name} — abrir menu de perfil`}
        data-testid="profile-menu-avatar"
        onClick={() => (open ? fechar(true) : abrir("primeiro"))}
        onKeyDown={aoTeclarNoAvatar}
      >
        <span className="acertei-profile-menu__circle" aria-hidden>
          {initialOf(name)}
          <span
            className="acertei-profile-menu__status-dot acertei-profile-menu__status-dot--sm"
            data-presence={presence}
            data-testid="profile-menu-trigger-status"
          />
        </span>

        {/* Decorativo: o conteúdo abaixo repete o que o aria-label já diz. */}
        <span
          className="acertei-profile-menu__peek"
          data-testid="profile-menu-peek"
          aria-hidden
        >
          <span className="acertei-profile-menu__peek-name">{name}</span>
          <span className="acertei-profile-menu__peek-status">
            <span
              className="acertei-profile-menu__peek-dot"
              data-presence={presence}
            />
            {presence === "online" ? "Online" : "Ausente"}
          </span>
        </span>
      </button>

      {shortcutsOpen && (
        <ShortcutsDialog onClose={() => setShortcutsOpen(false)} />
      )}
    </div>
  );
}

/**
 * Diálogo de atalhos.
 *
 * O produto não tem um — o spec autoriza criar. É deliberadamente mínimo: lista
 * o que o próprio menu registra, e não inventa um inventário de atalhos do app
 * que ninguém mantém.
 */
function ShortcutsDialog({ onClose }: { onClose: () => void }) {
  const mod = isMacLike() ? "⌘" : "Ctrl";
  const linhas: Array<[string, string]> = [
    [`${mod} ,`, "Abrir configurações"],
    ["?", "Mostrar esta lista"],
    ["Esc", "Fechar o menu de perfil"],
    ["↑ ↓", "Navegar pelos itens do menu"],
  ];

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="profile-menu-shortcuts"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Atalhos do teclado"
        className="w-full max-w-sm rounded-card border border-edge bg-surface p-5 shadow-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-3 text-sm font-semibold text-ink">
          Atalhos do teclado
        </h2>
        <ul className="m-0 list-none space-y-2 p-0">
          {linhas.map(([tecla, descricao]) => (
            <li
              key={tecla}
              className="flex items-center justify-between gap-4 text-[13px] text-ink-secondary"
            >
              <span>{descricao}</span>
              <kbd className="rounded-chip border border-edge bg-surface-muted px-2 py-0.5 text-[11px] text-ink">
                {tecla}
              </kbd>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-input border border-edge-strong bg-surface-muted py-2 text-[13px] font-semibold text-ink hover:bg-surface-hover"
        >
          Fechar
        </button>
      </div>
    </div>
  );
}
